/**
 * Animate an element's horizontal scroll position to `target` over
 * `duration` ms with an ease-in-out curve, driven by requestAnimationFrame.
 *
 * Why not `el.scrollTo({ behavior: 'smooth' })`? The Gantt timelines
 * render on a very wide (±1-year ≈ 40k-px) canvas. The browser's native
 * smooth scroll over a multi-thousand-pixel distance is slow and janky,
 * and its duration isn't controllable. A fixed-duration rAF tween stays
 * smooth and consistent no matter how far the jump is.
 *
 * Returns a cancel function (call it to stop the animation early).
 *
 * @param {HTMLElement} el        Scroll container.
 * @param {number}      target    Destination scrollLeft (clamped to ≥ 0).
 * @param {number}      [duration] Animation length in ms (default 450).
 */
export function smoothScrollLeftTo(el, target, duration = 450) {
  if (!el) return () => {}
  const start = el.scrollLeft
  const dest = Math.max(0, target)
  const dist = dest - start
  // Already there — nothing to animate.
  if (Math.abs(dist) < 1) {
    el.scrollLeft = dest
    return () => {}
  }

  let raf = null
  let startTime = null
  // easeInOutQuad — gentle acceleration then deceleration.
  const ease = (t) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2)

  const step = (now) => {
    if (startTime === null) startTime = now
    const p = Math.min(1, (now - startTime) / duration)
    el.scrollLeft = start + dist * ease(p)
    if (p < 1) {
      raf = requestAnimationFrame(step)
    } else {
      raf = null
    }
  }
  raf = requestAnimationFrame(step)

  return () => {
    if (raf != null) cancelAnimationFrame(raf)
  }
}
